// ============================================================================
// CLIENT GROUP HOOKS
// ============================================================================
// React hooks for the ClientGroup master.
//
// Pattern follows useEngagementTypes.ts:
// - useClientGroups(params)            → list with pagination, refetch
// - useClientGroup(id)                 → single record by id
// - useCreateClientGroup()             → create mutation
// - useUpdateClientGroup()             → update mutation
// - useDeleteClientGroup()             → delete mutation
//
// Note: useClientGroups serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { clientGroupService } from 'api/services/clientGroupService';
import type {
  ID,
  PaginatedResponse,
  ClientGroup,
  CreateClientGroupRequest,
  UpdateClientGroupRequest,
  ClientGroupSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of client groups.
 * Refetches automatically when `params` changes.
 */
export const useClientGroups = (params?: ClientGroupSearchRequest) => {
  const [data, setData] = useState<ClientGroup[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<ClientGroup>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchClientGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientGroupService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchClientGroups();
  }, [fetchClientGroups]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchClientGroups,
  };
};

/**
 * Hook to fetch a single client group by id.
 */
export const useClientGroup = (id: ID | null | undefined) => {
  const [data, setData] = useState<ClientGroup | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchClientGroup = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await clientGroupService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchClientGroup();
  }, [fetchClientGroup]);

  return { data, loading, error, refetch: fetchClientGroup };
};

/**
 * Hook to create a new client group.
 */
export const useCreateClientGroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createClientGroup = async (
    data: CreateClientGroupRequest,
    options?: {
      onSuccess?: (item: ClientGroup) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientGroupService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createClientGroup, loading, error, success };
};

/**
 * Hook to update a client group. Always pass the latest rowVersion.
 */
export const useUpdateClientGroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateClientGroup = async (
    id: ID,
    data: UpdateClientGroupRequest,
    options?: {
      onSuccess?: (item: ClientGroup) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await clientGroupService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateClientGroup, loading, error, success };
};

/**
 * Hook to delete a client group (soft-delete on the server).
 */
export const useDeleteClientGroup = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteClientGroup = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await clientGroupService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteClientGroup, loading, error, success };
};
